/**
 * Model ID Resolver
 *
 * Provides robust, future-proof matching between:
 * - User-facing model IDs (e.g., 'deepseek', 'gemini-pro')
 * - Database model IDs (e.g., 'model-deepseek-deepseek-chat')
 * - API model names (e.g., 'deepseek-chat', 'gemini-2.5-flash')
 *
 * This is the single source of truth for model ID matching.
 * Adding new models only requires updating MODEL_CONFIG.
 */

interface ModelConfig {
  userModelId: string;
  apiModelName: string;
  dbModelIdPattern: RegExp;
  provider: string;
  displayName: string;
}

/**
 * Central model configuration
 * Maps user-facing model IDs to their backend equivalents
 * Updated 2025-10-30: Post-migration database (6 active models)
 */
export const MODEL_CONFIG: Record<string, ModelConfig> = {
  'gpt-4': {
    userModelId: 'gpt-4',
    apiModelName: 'gpt-4o-mini',
    dbModelIdPattern: /^model-openai-gpt-4/i,
    provider: 'openai',
    displayName: 'GPT-4',
  },
  'claude-3': {
    userModelId: 'claude-3',
    apiModelName: 'claude-haiku-4-5-20251001',
    dbModelIdPattern: /^model-anthropic-claude-3/i,
    provider: 'anthropic',
    displayName: 'Claude',
  },
  'gemini-pro': {
    userModelId: 'gemini-pro',
    apiModelName: 'gemini-2.0-flash',
    dbModelIdPattern: /^model-google-gemini/i,
    provider: 'google',
    displayName: 'Gemini',
  },
  'gemini': {
    userModelId: 'gemini',
    apiModelName: 'gemini-2.0-flash',
    dbModelIdPattern: /^model-google-gemini/i,
    provider: 'google',
    displayName: 'Gemini',
  },
  'deepseek': {
    userModelId: 'deepseek',
    apiModelName: 'deepseek-chat',
    dbModelIdPattern: /^model-deepseek/i,
    provider: 'deepseek',
    displayName: 'DeepSeek',
  },
  'grok': {
    userModelId: 'grok',
    apiModelName: 'grok-3-mini',
    dbModelIdPattern: /^model-xai-grok/i,
    provider: 'xai',
    displayName: 'Grok',
  },
  'llama-3': {
    userModelId: 'llama-3',
    apiModelName: 'Llama-3.3-70B-Instruct',
    dbModelIdPattern: /^model-meta-llama/i,
    provider: 'meta',
    displayName: 'Llama',
  },
};

/**
 * Determines if a response matches a requested model ID
 *
 * This is the ONLY function that should be used to check if a response
 * belongs to a requested model. It handles all edge cases and ID formats.
 *
 * @param requestedModelId - The model ID from comparison.modelsUsed (e.g., 'deepseek')
 * @param response - The response object with modelId and optional model.name
 * @returns true if the response matches the requested model
 */
export function doesResponseMatchRequestedModel(
  requestedModelId: string,
  response: { modelId: string; model?: { name?: string | null } | null }
): boolean {
  const mapping = MODEL_CONFIG[requestedModelId];

  if (!mapping) {
    // Fallback for models not in config: try exact match or contains match
    const normalized = requestedModelId.toLowerCase();
    const responseIdLower = response.modelId.toLowerCase();

    // Exact match
    if (normalized === responseIdLower) {
      return true;
    }

    // Contains match (for backwards compatibility)
    if (responseIdLower.includes(normalized)) {
      return true;
    }

    return false;
  }

  // Priority 1: DB model ID pattern match (most reliable)
  if (mapping.dbModelIdPattern.test(response.modelId)) {
    return true;
  }

  // Priority 2: API model name match
  if (response.model?.name && response.model.name === mapping.apiModelName) {
    return true;
  }

  // Priority 3: Exact match with user model ID
  if (requestedModelId === response.modelId) {
    return true;
  }

  return false;
}

/**
 * Get all model IDs that should be considered equivalent
 * Useful for debugging and testing
 */
export function getEquivalentModelIds(requestedModelId: string): string[] {
  const mapping = MODEL_CONFIG[requestedModelId];

  if (!mapping) {
    return [requestedModelId];
  }

  return [
    mapping.userModelId,
    mapping.apiModelName,
    // DB pattern would match multiple IDs, so we can't list them all
  ];
}

/**
 * Extract provider name from model ID
 * Used for logo mapping and display
 */
export function extractProviderFromModelId(modelId: string): string {
  const lowerModelId = modelId.toLowerCase();

  // Check each config entry's pattern
  for (const config of Object.values(MODEL_CONFIG)) {
    if (config.dbModelIdPattern.test(modelId)) {
      return config.provider;
    }
  }

  // Fallback: try to extract from model ID string
  if (lowerModelId.includes('openai') || lowerModelId.includes('gpt')) return 'openai';
  if (lowerModelId.includes('anthropic') || lowerModelId.includes('claude')) return 'anthropic';
  if (lowerModelId.includes('google') || lowerModelId.includes('gemini')) return 'google';
  if (lowerModelId.includes('deepseek')) return 'deepseek';
  if (lowerModelId.includes('xai') || lowerModelId.includes('grok')) return 'xai';
  if (lowerModelId.includes('meta') || lowerModelId.includes('llama')) return 'meta';

  return 'unknown';
}
